import { initializeApp } from "@firebase/app";
import ApiService, { checkSamlType, validatePhone } from "./ApiService";
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  SAMLAuthProvider,
  OAuthProvider,
  getRedirectResult,
  useDeviceLanguage,
  signInWithEmailAndPassword,
  setPersistence,
  inMemoryPersistence,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  FacebookAuthProvider,
  signInWithCustomToken,
  onAuthStateChanged,
  signOut,
  updatePassword,
} from "@firebase/auth";
import SystemPromptService from "./systemPromptService";
import {
  MOBILE_HOSTS,
  PROVIDERS_TYPE,
  FIREBASE_CONFIG,
  LOG_SEVERITIES,
  MESSAGES_TYPES,
  REDIRECT_TYPES,
  OTP_EVENTS,
  USER_TYPES,
  AUTH_ABSENT_TOKEN_STUB,
  LOCAL_STORAGE_KEYS,
  SAML_PROVIDER_CLALIT,
  SAML_PROVIDER_LEUMIT,
  ALLOWED_SAML_TYPES,
  URL_PARAMS,
} from "../assets/appConstants";
import {ALERTS} from "../components/common/systemAlert";
import {getAnalytics} from "@firebase/analytics";
import {
  postMessage,
  redirect,
  validateUserTypeMatchesApp,
  isClientMobile,
  getUrlParam,
  getAppType,
} from "./utils";
import mixPanelService from "./mixPanel/mixPanelService";
import {
  isClinicianAccountType,
  resolveAccountContext,
} from "./accountTypeResolver";
import { resolveClinicianLoginSession } from "./clinicianLoginSession";

//<editor-fold desc="init firebase">
SystemPromptService.showSpinner();

const app = initializeApp(FIREBASE_CONFIG());
export const auth = getAuth(app);

getRedirectResult(auth)
  .then((res) => {
    if (res) {
      postMessage(MESSAGES_TYPES.PROVIDER_LOGIN_END);
      continueLogin(res);
    }
  })
  .catch((error) => {
    firebaseErrorHandling(error);
  });

getAnalytics(app);

setPersistence(auth, inMemoryPersistence).catch((error) => {
  mixPanelService.trackUserOnHold(error);
});

// const analytics = getAnalytics(app);
//</editor-fold>

const MICROSOFT_LOGOUT_URL =
  "https://login.microsoftonline.com/common/oauth2/v2.0/logout";

function getPostLogoutRedirectUri() {
  let returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  try {
    if (window.top && window.top !== window.self) {
      const topWin = window.top;
      returnUrl = `${topWin.location.origin}${topWin.location.pathname}${topWin.location.search}`;
    }
  } catch (e) {
    /* cross-origin parent */
  }
  return returnUrl;
}

export function getMicrosoftLogoutUrl() {
  const returnUrl = getPostLogoutRedirectUri();
  return `${MICROSOFT_LOGOUT_URL}?post_logout_redirect_uri=${encodeURIComponent(returnUrl)}`;
}

/** Opens Microsoft logout in a small popup (Microsoft blocks iframe embedding via X-Frame-Options). */
export function openMicrosoftLogoutPopup(logoutUrl) {
  const w = 480;
  const h = 640;
  const left = Math.max(0, (window.screen.width - w) / 2);
  const top = Math.max(0, (window.screen.height - h) / 2);
  const features = `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`;
  return window.open(logoutUrl, "microsoftLogout", features);
}

/**
 * Signs out Firebase, clears remembered SSO + tab session.
 */
export async function clearSsoAccountState(onBeforeClear) {
  try {
    if (auth.currentUser) {
      await signOut(auth);
    }
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_SAML_PROVIDER);
    sessionStorage.clear();
    if (typeof onBeforeClear === "function") {
      onBeforeClear();
    }
  } catch (e) {
    ApiService.logIt(`clearSsoAccountState: ${e?.message || e}`, LOG_SEVERITIES.WARNING);
  }
}

//<editor-fold desc="login options">
export const firebaseProviderLogin = (providerType, phoneNumber) => {
  postMessage(MESSAGES_TYPES.PROVIDER_LOGIN);

  let provider;
  switch (providerType) {
    case PROVIDERS_TYPE.GOOGLE:
      ApiService.logIt("login with google");
      provider = new GoogleAuthProvider();
      break;
    case PROVIDERS_TYPE.SAML:
      ApiService.logIt("login with saml");
      // Get the last used SAML provider from localStorage
      const lastSamlProvider = localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_SAML_PROVIDER);
      let samlProviderId = "saml.clalit"; // default to clalit
      if (lastSamlProvider === SAML_PROVIDER_LEUMIT) {
        samlProviderId = "saml.leumit";
      }
      provider = new SAMLAuthProvider(samlProviderId);
      break;
    case PROVIDERS_TYPE.APPLE:
      ApiService.logIt("login with apple");
      provider = new OAuthProvider("apple.com");
      break;
    case PROVIDERS_TYPE.FACEBOOK:
      ApiService.logIt("login with facebook");
      provider = new FacebookAuthProvider();
      break;
    case PROVIDERS_TYPE.OTP:
      ApiService.logIt("login with OTP");
      firebaseOneTimePassword(phoneNumber);
      break;

    default:
      ApiService.logIt(`no argument passed`, LOG_SEVERITIES.WARNING);
      return;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useDeviceLanguage(auth);

  if (isClientMobile()) {
    signInWithRedirect(auth, provider)
      .then((res) => {
        mixPanelService.trackSSOSuccess(providerType);
        continueLogin(res);
      })
      .catch((error) => {
        mixPanelService.trackSSOFail(error.message);
        firebaseErrorHandling(error);
      });
  } else {
    signInWithPopup(auth, provider)
      .then((res) => {
        mixPanelService.trackLoginSuccess(providerType);
        continueLogin(res);
      })
      .catch((error) => {
        mixPanelService.trackLoginFailure(providerType, error.message);
        firebaseErrorHandling(error);
      });
  }
};

export const firebasePasswordLogin = (identifier, password) => {
  ApiService.logIt(`login with password email is: ${identifier}`);
  SystemPromptService.showSpinner();
  if (validatePhone(identifier)) {
    return ApiService.phoneLogin(identifier, password)
      .then((res) => {
        mixPanelService.trackLoginSuccess("phone and password");
        finishLogin(res);
      })
      .catch((error) => {
        SystemPromptService.hideSpinner();
        firebaseErrorHandling(error);
      });
  }

  return signInWithEmailAndPassword(auth, identifier, password)
    .then((res) => {
      mixPanelService.trackLoginSuccess("email and password");
      return continueLogin(res);
    })
    .catch((error) => {
      SystemPromptService.hideSpinner();
      firebaseErrorHandling(error);
    });
};

export const LoginWithCustomToken = (customToken) => {
  return signInWithCustomToken(auth, customToken);
};

// //OTP function
export const firebaseOneTimePassword = (phoneNumber) => {
  return new Promise((resolve, reject) => {
    if (!window.firebaseAuth) {
      window.firebaseAuth = getAuth();
    }

    const recaptchaContainer = document.getElementById("recaptcha-container");
    if (recaptchaContainer) {
      recaptchaContainer.remove();
    }

    document.body.appendChild(Object.assign(document.createElement('div'), {id: 'recaptcha-container'}));

    if (window.recaptchaVerifier) {
      window.recaptchaVerifier = null;
    }

    window.recaptchaVerifier = new RecaptchaVerifier(
      "recaptcha-container",
      {
        size: "invisible",
        callback: (response) => {
          // reCAPTCHA solved, allow signInWithPhoneNumber.
        },
      },
      window.firebaseAuth
    );

    signInWithPhoneNumber(
      window.firebaseAuth,
      phoneNumber,
      window.recaptchaVerifier
    )
      .then((confirmationResult) => {
        mixPanelService.trackOTPSuccess();
        resolve(confirmationResult);
      })
      .catch((error) => {
        console.error("Error during sign-in", error);
        //log it
        ApiService.logOtpEvent(OTP_EVENTS.SMS_SEND_ERROR, {phoneNumber, error});
        mixPanelService.trackOTPFailure(error.message);
        reject(error);
      });
  });
};

//</editor-fold>

//<editor-fold desc="login handlers">
let continueLoginInProgress = false;

export function continueLogin(userCredentials) {
  const user = userCredentials.user;
  const providerId = user.providerData?.[0]?.providerId || "";

  if (continueLoginInProgress) {
    return Promise.resolve();
  }
  continueLoginInProgress = true;

  return (async () => {
    const accountTypeHint = getAppType();
    // Single my/type (+ claims) resolution — reused by clinician session.
    const accountContext = await resolveAccountContext(user, accountTypeHint);

    if (isClinicianAccountType(accountContext.accountType)) {
      const session = await resolveClinicianLoginSession(user, {
        accountTypeHint,
        accountContext,
      });
      const loginData = { ...session.loginData };
      if (ALLOWED_SAML_TYPES.includes(providerId)) {
        loginData.providerType = PROVIDERS_TYPE.SAML;
        loginData.samlProviderId = providerId;
      }
      finishLogin(loginData);
      return;
    }

    const idToken = await user.getIdToken();
    const res = await ApiService.loginHelper(user.uid, idToken);
    if (!res) {
      throw new Error("Login helper returned an empty response");
    }
    if (ALLOWED_SAML_TYPES.includes(providerId)) {
      res.providerType = PROVIDERS_TYPE.SAML;
      res.samlProviderId = providerId;
    }
    finishLogin(res);
  })()
    .catch((error) => {
      ApiService.logIt(
        "continueLogin failed: " + ApiService.findErrorDescription(error),
        LOG_SEVERITIES.WARNING,
        error
      );
      if (error?.response || error?.data) {
        ApiService.handleError(error);
        return;
      }
      firebaseErrorHandling(error);
    })
    .finally(() => {
      continueLoginInProgress = false;
      SystemPromptService.hideSpinner();
      postMessage(MESSAGES_TYPES.HIDE_PRELOADER);
    });
}

export const finishLogin = (loginData) => {
  // Save the provider type to localStorage if it was a SAML login
  if (loginData.providerType === PROVIDERS_TYPE.SAML) {
    // Map SAML provider ID to provider constant
    let providerName = SAML_PROVIDER_CLALIT;
    if (loginData.samlProviderId === "saml.leumit") {
      providerName = SAML_PROVIDER_LEUMIT;
    }
    localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_SAML_PROVIDER, providerName);
  }

  // Validate that user type matches app type
  if (!validateUserTypeMatchesApp(loginData)) {
    const accountType = loginData.accountType?.toLowerCase();
    let correctAppType;
    
    if (accountType === USER_TYPES.THERAPIST) {
      correctAppType = USER_TYPES.CLINICIAN;
    } else if (accountType === USER_TYPES.PATIENT) {
      correctAppType = USER_TYPES.PATIENT;
    }

    const hasAppTypeParam = !!getUrlParam(URL_PARAMS.APPTYPE, true);
    const isMobile = isClientMobile();

    if (isMobile) {
      SystemPromptService.showAlert({
        title: "wrong.app.title",
        content: `wrong.app.message.${correctAppType}`,
        btnCloseTitle: "close",
        btnAction: { show: false },
      });
      return;
    }

    const redirectParams = {};
    if (hasAppTypeParam) {
      redirectParams[URL_PARAMS.APPTYPE] = correctAppType;
    }
    SystemPromptService.showAlert({
      title: "wrong.app.title",
      content: `wrong.app.message.${correctAppType}`,
      btnCloseTitle: "close",
      hideCloseBtn: true,
      btnAction: {
        show: true,
        action: () => {
          postMessage(MESSAGES_TYPES.BACK_HOME);
          redirect(REDIRECT_TYPES.HOME, redirectParams);
        },
        title: "close"
      },
    });
    return;
  }
  
  if (loginData.accountData) {
    // Incomplete registration → onboarding wizard (not main-app redirect).
    const accountObj = loginData.accountData;
    accountObj.accountId = loginData.accountId;

    accountObj.loginData = {
      customToken: loginData.customToken,
    };
    loginData.immediateRedirect = false;
    postMessage(MESSAGES_TYPES.LOGIN_SUCCESS, loginData);
    redirect(REDIRECT_TYPES.ONBOARDING, {
      customToken: loginData.customToken,
      ...(isClinicianAccountType(loginData.accountType)
        ? { [URL_PARAMS.APPTYPE]: USER_TYPES.CLINICIAN }
        : {}),
    });
    return;
  }

  // Completed login → parent app handles main redirect via LOGIN_SUCCESS.
  // Keep the same shape login-helper used to send (parent gates on these fields).
  const redirectPayload = {
    ...loginData,
    accountId: loginData.accountId,
    accountType: isClinicianAccountType(loginData.accountType)
      ? USER_TYPES.THERAPIST
      : loginData.accountType,
    customToken: loginData.customToken,
    encryptedAccountId:
      loginData.encryptedAccountId ||
      loginData.subAccountId ||
      loginData.accountId ||
      null,
    subAccountId: loginData.subAccountId || null,
    accountData: null,
    immediateRedirect: true,
    registrationCompleted:
      loginData.registrationCompleted !== false ? true : false,
  };

  if (!redirectPayload.customToken) {
    ApiService.logIt(
      "finishLogin missing customToken for clinician redirect",
      LOG_SEVERITIES.WARNING,
      redirectPayload
    );
    return;
  }

  postMessage(MESSAGES_TYPES.LOGIN_SUCCESS, redirectPayload);
};
//</editor-fold>

//<editor-fold desc="error handling">
function firebaseErrorHandling(error) {
  ApiService.logIt(
    "caught firebase error:" + ApiService.findErrorDescription(error),
    LOG_SEVERITIES.NOTICE,
    error
  );
  const errorMessage = error.message;
  let message, title;
  switch (true) {
    case errorMessage.indexOf("auth/wrong-password") >= 0:
    case errorMessage.indexOf("auth/invalid-credential") >= 0:
    case errorMessage.indexOf("auth/invalid-login-credentials") >= 0:
    case errorMessage.indexOf("INVALID_LOGIN_CREDENTIALS") >= 0:
      title = "login.failed.title";
      message = "login.failed.message";
      mixPanelService.trackLoginFailure("password", "Wrong password");
      break;
    case errorMessage.indexOf("auth/invalid-email") >= 0:
      title = "invalid.email.title";
      message = "invalid.email.message";
      mixPanelService.trackLoginFailure("email", "Invalid email");
      break;
    case errorMessage.indexOf("auth/user-not-found") >= 0:
    case errorMessage.indexOf("auth/admin-restricted-operation") >= 0:
      title = "login.failed.title";
      message = "login.failed.message";
      mixPanelService.trackLoginFailure("email", "User not found");
      break;
    case errorMessage.indexOf("auth/user-disabled") >= 0:
      title = "disabled.account.title";
      message = "disabled.account.message";
      mixPanelService.trackUserOnHold();
      break;
    case errorMessage.indexOf("auth/network-request-failed") >= 0:
      title = "network.issue.title";
      message = "network.issue.message";
      break;
    default:
      message = "general.message";
      title = "general.title";
  }
  SystemPromptService.showAlert({
    title,
    content: message,
    btnCloseTitle: "close",
    btnAction: {
      show: false,
    },
  });
}

//</editor-fold>

export const getIdToken = async function () {
  if (auth.currentUser) {
    return auth.currentUser.getIdToken();
  }

  return await new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        user
          .getIdToken()
          .then((payload) => {
            resolve(payload);
          })
          .catch((err) => {
            reject(err);
          });
      } else {
        resolve(AUTH_ABSENT_TOKEN_STUB);
      }
    });
  });
};

export const getFirebaseUser = async function (customToken = null) {
//if auth user is null, wait for firebase to load
  return await new Promise(async (resolve, reject) => {
    if (customToken) {
      await signInWithCustomToken(auth, customToken);
    }
    onAuthStateChanged(auth, (user) => {
      if (user) {
        resolve(user);
      } else {
        reject(AUTH_ABSENT_TOKEN_STUB);
      }
    });
  });
};

export const updateFirebasePassword = (password) => {
  const user = auth.currentUser;
  if (!user) {
    return Promise.reject(new Error("No authenticated user"));
  }
  return updatePassword(user, password);
};

export const getSamlProvider = (email) => {
  const samlType = checkSamlType(email);
  if (samlType) {
    const provider = new SAMLAuthProvider(samlType);

    if (isClientMobile()) {
      signInWithRedirect(auth, provider)
        .then((res) => {
          mixPanelService.trackSSOSuccess(samlType);
          continueLogin(res);
        })
        .catch((error) => {
          mixPanelService.trackSSOFail(error.message);
          firebaseErrorHandling(error);
        });
    } else {
      signInWithPopup(auth, provider)
        .then((res) => {
          mixPanelService.trackSSOSuccess(samlType);
          continueLogin(res);
        })
        .catch((error) => {
          mixPanelService.trackSSOFail(error.message);
          firebaseErrorHandling(error);
        });
    }
    return provider;
  }
  return null;
};
